import { defineStore, acceptHMRUpdate } from 'pinia'

type Nullable<T> = T | null

interface UserShape {
    firstName: string
    lastName: string
    emailAddress: string
    phoneNumber: string
    balance: number
    role: string
    buildingName: Nullable<string>
    streetName: Nullable<string>
    city: Nullable<string>
    postCode: Nullable<string>
    state: Nullable<string>
    country: Nullable<string>
    profilePicture: Nullable<string>
    emailMfa: boolean | null
    emailMfaExpiry: Nullable<string>
    isVerified: boolean
    nextResendAllowedTime: Nullable<string>
    activeAccountDeletionRequest: boolean
}

function defaultUser(): UserShape {
    return {
        firstName: 'No',
        lastName: 'Auth',
        emailAddress: 'noauth@null.net',
        phoneNumber: '+44 7777 777777',
        balance: 0.0,
        role: 'Customer',
        buildingName: null,
        streetName: null,
        city: null,
        postCode: null,
        state: null,
        country: null,
        profilePicture: null,
        emailMfa: false,
        emailMfaExpiry: null,
        isVerified: false,
        nextResendAllowedTime: null,
        activeAccountDeletionRequest: false,
    }
}

export const useUserDataStore = defineStore(
    'userData',
    () => {
        // State
        const user = reactive<UserShape>(defaultUser())
        const serverCount = ref<number>(0)
        const openTicketCount = ref<number>(0)
        const invoiceCount = ref<number>(0)
        const lastActivity = ref<any[]>([])

        // Actions
        async function fetchUserData(authToken: string): Promise<void> {
            if (!authToken) {
                console.warn('JWT is not set, skipping user data fetch.')
                return
            }

            try {
                if (import.meta.client) {
                    const response = await fetch('/api/v1/userdata', {
                        method: 'GET',
                        headers: { Authorization: `Bearer ${authToken}` },
                    })
                    if (!response.ok) throw new Error('Failed to fetch user data')

                    const data = await response.json()

                    Object.assign(user, {
                        firstName: data.user.firstName,
                        lastName: data.user.lastName,
                        emailAddress: data.user.emailAddress,
                        phoneNumber: data.user.phoneNumber,
                        balance: data.user.balance,
                        role: data.user.role,
                        buildingName: data.user.buildingName,
                        streetName: data.user.streetName,
                        city: data.user.city,
                        postCode: data.user.postCode,
                        state: data.user.state,
                        country: data.user.country,
                        profilePicture: data.user.profilePicture,
                        emailMfa: data.user.emailMfa,
                        emailMfaExpiry: data.user.emailMfaExpiry,
                        isVerified: data.user.isVerified,
                        nextResendAllowedTime: data.user.nextResendAllowedTime,
                        activeAccountDeletionRequest: data.user.activeAccountDeletionRequest,
                    })

                    serverCount.value = data.serverCount ?? 0
                    openTicketCount.value = data.openTicketCount ?? 0
                    invoiceCount.value = data.invoiceCount ?? 0
                    lastActivity.value = data.lastActivity ?? []
                }
            } catch (err) {
                console.error('Error fetching user data:', err)
            }
        }

        function resetUserData(): void {
            Object.assign(user, defaultUser())
            serverCount.value = 0
            openTicketCount.value = 0
            invoiceCount.value = 0
            lastActivity.value = []
        }

        return {
            // state
            user,
            serverCount,
            openTicketCount,
            invoiceCount,
            lastActivity,
            // actions
            fetchUserData,
            resetUserData,
        }
    },
    { persist: true }
)

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useUserDataStore, import.meta.hot))
}
